import React, {createContext, useContext, useEffect, useState} from 'react';
import {GhostApi} from './utils/api';

export interface CommentSettings {
    allow_replies: boolean;
    text_color: string;
    input_bg_color: string;
    prompt_upgrade: string;
    prompt_signup: string;
    prompt_already_member: string;
    prompt_signin: string;
}

// デフォルト値の設定
const defaultCommentSettings: CommentSettings = {
    allow_replies: true,
    text_color: '#333333',
    input_bg_color: '#ffffff',
    prompt_upgrade: '',
    prompt_signup: '',
    prompt_already_member: '',
    prompt_signin: ''
};

export interface CommentSettingsContextData {
    settings: CommentSettings;
    loading: boolean;
    error: Error | null;
}

// Context の作成
export const CommentSettingsContext = createContext<CommentSettingsContextData>({
    settings: defaultCommentSettings,
    loading: true,
    error: null
});

// Provider コンポーネント
export const CommentSettingsProvider: React.FC<{
    children: React.ReactNode;
    api: GhostApi;
}> = ({children, api}) => {
    const [state, setState] = useState<CommentSettingsContextData>({
        settings: defaultCommentSettings,
        loading: true,
        error: null
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.site.settings();
                const commentSettings = {
                    allow_replies: data.settings.allow_replies === true || data.settings.allow_replies === 'true',
                    text_color: data.settings.text_color || defaultCommentSettings.text_color,
                    input_bg_color: data.settings.input_bg_color || defaultCommentSettings.input_bg_color,
                    prompt_upgrade: data.settings.prompt_upgrade || defaultCommentSettings.prompt_upgrade,
                    prompt_signup: data.settings.prompt_signup || defaultCommentSettings.prompt_signup,
                    prompt_already_member: data.settings.prompt_already_member || defaultCommentSettings.prompt_already_member,
                    prompt_signin: data.settings.prompt_signin || defaultCommentSettings.prompt_signin
                };
                setState({
                    settings: {
                        ...defaultCommentSettings,
                        ...commentSettings
                    },
                    loading: false,
                    error: null
                });
            } catch (error) {
                setState({
                    settings: defaultCommentSettings,
                    loading: false,
                    error: error instanceof Error ? error : new Error('Unknown error occurred')
                });
                // eslint-disable-next-line no-console
                console.error('[Comments] Failed to fetch comment settings:', error);
            }
        };

        fetchSettings();
    }, [api]);

    return (
        <CommentSettingsContext.Provider value={state}>
            {children}
        </CommentSettingsContext.Provider>
    );
};

// 便利なフック
export const useCommentSettings = () => useContext(CommentSettingsContext);

// スタイルを CSS 変数として注入するフック
export const useInjectCommentStyles = () => {
    const {settings} = useCommentSettings();

    useEffect(() => {
        // ルート要素（:root または body など）に CSS 変数を設定
        document.documentElement.style.setProperty('--comment-text-color', settings.text_color);
        document.documentElement.style.setProperty('--comment-input-bg-color', settings.input_bg_color);
    }, [settings]);

    return settings;
};
